export type TaxMode = "inherit" | "none" | "override";
export type DocumentTaxDefinition = Readonly<{
  id: string;
  name: string;
  rateBasisPoints: number;
  taxNumber: string | null;
  savedRateId: string | null;
}>;
export type DocumentTaxSet = readonly string[];
export type DocumentTaxes = {
  definitions: readonly DocumentTaxDefinition[];
  defaultTaxSet: DocumentTaxSet;
};
export type LineTaxSelection =
  | { mode: "inherit" }
  | { mode: "none" }
  | { mode: "override"; taxSet: DocumentTaxSet };
export type CalculatedTax = Readonly<{
  definition: DocumentTaxDefinition;
  taxableBaseMinor: number;
  amountMinor: number;
}>;
export class DocumentTaxError extends Error {}

const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
function object(value: unknown, keys: string[]): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value) || Object.keys(value).some(key => !keys.includes(key))) throw new DocumentTaxError("Invalid tax data");
  return value as Record<string, unknown>;
}
function identifier(value: unknown): string {
  if (typeof value !== "string" || !uuid.test(value)) throw new DocumentTaxError("Invalid tax identifier");
  return value;
}
function text(value: unknown, optional = false): string | null {
  if (optional && (value === null || value === undefined || value === "")) return null;
  if (typeof value !== "string" || !value.trim() || value.trim().length > 120 || value.includes("\0")) throw new DocumentTaxError("Invalid tax text");
  return value.trim();
}
export function parseTaxSet(value: unknown): string[] {
  if (!Array.isArray(value) || value.length > 100) throw new DocumentTaxError("Invalid tax set");
  const ids = value.map(identifier);
  if (new Set(ids).size !== ids.length) throw new DocumentTaxError("Duplicate tax selection");
  return ids;
}
export function parseDocumentTaxes(value: unknown): DocumentTaxes {
  const input = object(value, ["definitions", "defaultTaxSet"]);
  if (!Array.isArray(input.definitions) || input.definitions.length > 1000) throw new DocumentTaxError("Invalid tax definitions");
  const definitions = input.definitions.map(value => {
    const tax = object(value, ["id", "name", "rateBasisPoints", "taxNumber", "savedRateId"]);
    if (!Number.isInteger(tax.rateBasisPoints) || Number(tax.rateBasisPoints) < 1 || Number(tax.rateBasisPoints) > 10000) throw new DocumentTaxError("Invalid tax rate");
    return { id: identifier(tax.id), name: text(tax.name)!, rateBasisPoints: Number(tax.rateBasisPoints), taxNumber: text(tax.taxNumber, true), savedRateId: tax.savedRateId == null ? null : identifier(tax.savedRateId) };
  });
  if (new Set(definitions.map(tax => tax.id)).size !== definitions.length) throw new DocumentTaxError("Duplicate tax definition");
  const taxes = { definitions, defaultTaxSet: parseTaxSet(input.defaultTaxSet) };
  resolveTaxSet(taxes, taxes.defaultTaxSet);
  return taxes;
}
export function resolveTaxSet(taxes: DocumentTaxes, ids: DocumentTaxSet): DocumentTaxDefinition[] {
  if (new Set(ids).size !== ids.length) throw new DocumentTaxError("Duplicate tax selection");
  return ids.map(id => {
    const tax = taxes.definitions.find(tax => tax.id === id);
    if (!tax) throw new DocumentTaxError("Tax definition not found in this document");
    return tax;
  });
}
export function resolveLineTaxes(taxes: DocumentTaxes, mode: TaxMode, taxSet: DocumentTaxSet = []): DocumentTaxDefinition[] {
  if (!["inherit", "none", "override"].includes(mode) || (mode !== "override" && taxSet.length) || (mode === "override" && !taxSet.length)) throw new DocumentTaxError("Invalid line tax selection");
  return resolveTaxSet(taxes, mode === "inherit" ? taxes.defaultTaxSet : mode === "none" ? [] : taxSet);
}
// A definition ID is immutable. Adapt only the supplied editing set; all other
// sets still point to the old definition, which remains available in the document.
export function adaptDocumentTax(taxes: DocumentTaxes, editingSet: DocumentTaxSet, id: string, changes: Omit<DocumentTaxDefinition, "id">, newId = crypto.randomUUID()) {
  resolveTaxSet(taxes, editingSet);
  if (!editingSet.includes(id) || taxes.definitions.some(tax => tax.id === newId)) throw new DocumentTaxError("Invalid tax adaptation");
  const next = parseDocumentTaxes({ ...taxes, definitions: [...taxes.definitions, { ...changes, id: newId }] });
  return { taxes: next, taxSet: editingSet.map(selected => selected === id ? newId : selected) };
}
export function applyTaxesToAll<T extends { taxMode?: TaxMode; taxSet?: DocumentTaxSet; taxRateId: string | null }>(taxes: DocumentTaxes, taxSet: DocumentTaxSet, lines: readonly T[]) {
  resolveTaxSet(taxes, taxSet);
  return { taxes: { ...taxes, defaultTaxSet: [...taxSet] }, lines: lines.map(line => ({ ...line, taxMode: "inherit" as const, taxSet: [], taxRateId: null })) };
}
export function assertTaxDefinitionsPreserved(previous: DocumentTaxes, next: DocumentTaxes) {
  for (const old of previous.definitions) {
    const current = next.definitions.find(tax => tax.id === old.id);
    if (!current || current.name !== old.name || current.rateBasisPoints !== old.rateBasisPoints || current.taxNumber !== old.taxNumber || current.savedRateId !== old.savedRateId) throw new DocumentTaxError("Adapt taxes using a new local definition ID");
  }
}
